import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { useParams, useNavigate, NavLink, Outlet, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, Loader2, Calendar, Award, GraduationCap, TrendingUp, TrendingDown,
  BookOpen, ClipboardList, CalendarCheck, Wallet, Heart, MessageCircle, Bell,
  Sparkles, FileDown, Info, ShieldCheck, ClipboardCheck, CalendarClock,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, RadialBarChart, RadialBar,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import StudentFinancePanel from "@/components/finance/StudentFinancePanel";

const NAVY = "#1B2C5A";
const LIME = "#9DCD3F";
const AMBER = "#F59E0B";
const RED = "#DC2626";
const EMERALD = "#10B981";

const ChildDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [term, setTerm] = useState("first");
  const [reportCard, setReportCard] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [rcStatus, setRcStatus] = useState("draft"); // 'draft' | 'published' | 'error'
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentId, setEnrollmentId] = useState("");
  const loadVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    try {
      const [chRes, seRes] = await Promise.all([
        api.get(`/students/${id}`),
        api.get("/academic-sessions"),
      ]);
      if (version !== loadVersion.current) return;
      setChild(chRes.data); setSessions(seRes.data);
      const cur = seRes.data.find((s) => s.is_current) || seRes.data[0];
      if (cur) setSessionId(cur.id);
    } catch (e) { if (version === loadVersion.current) toast.error(formatApiError(e)); }
    finally { if (version === loadVersion.current) setLoading(false); }
  }, [id]);
  useEffect(() => {
    setChild(null);
    setSessionId("");
    setEnrollmentId("");
    setEnrollments([]);
    setReportCard(null);
    load();
    return () => { loadVersion.current += 1; };
  }, [load]);

  // load per-term data whenever term/session changes
  useEffect(() => {
    if (!sessionId || !id) return;
    let cancelled = false;
    setReportCard(null);
    setRcStatus("loading");
    (async () => {
      // multi-program enrollments (Conventional / Islamiyya / Tahfiz / Creche)
      let sessionEnrollments = null;
      try {
        const en = await api.get(`/students/${id}/enrollments`);
        if (cancelled) return;
        const items = Array.isArray(en.data) ? en.data : [];
        setEnrollments(items);
        sessionEnrollments = items.filter((e) => e.session_id === sessionId && ["active", "promoted"].includes(e.status));
      } catch (e) { if (!cancelled) setEnrollments([]); }
      // attendance is session-wide
      try { const a = await api.get(`/attendance/student/${id}`); if (!cancelled) setAttendance(a.data); } catch (e) { if (!cancelled) setAttendance(null); }
      // Finance is loaded placement-by-placement in the Fees tab from canonical v2 APIs.
      if (cancelled) return;
      setInvoices([]);
      // notifications (school-wide + role-scoped)
      try { const n = await api.get("/notifications"); if (!cancelled) setNotifications(n.data.slice(0, 20)); } catch (e) { if (!cancelled) setNotifications([]); }
      // report card (may 423 for parent on draft)
      try {
        if (cancelled) return;
        if (!sessionEnrollments) { setRcStatus("error"); return; }
        const placement = sessionEnrollments.find((e) => e.id === enrollmentId)
          || (sessionEnrollments.length === 1 ? sessionEnrollments[0] : null);
        if (!placement) {
          setRcStatus(sessionEnrollments.length > 1 ? "select-programme" : "notfound");
          return;
        }
        const r = await api.get(`/assessments/report-card/${id}`, {
          params: { session_id: sessionId, term, program_id: placement.program_id, class_id: placement.class_id, arm_id: placement.arm_id },
        });
        if (cancelled) return;
        setReportCard(r.data);
        setRcStatus(r.data.status || "published");
      } catch (e) {
        if (cancelled) return;
        setReportCard(null);
        const status = e?.response?.status;
        setRcStatus(status === 423 ? "draft" : status === 404 ? "notfound" : status === 409 ? "ambiguous" : status === 403 ? "forbidden" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, term, id, enrollmentId]);

  const eligibleEnrollments = enrollments.filter((e) => e.session_id === sessionId && ["active", "promoted"].includes(e.status));
  const selectedEnrollment = eligibleEnrollments.find((e) => e.id === enrollmentId)
    || (eligibleEnrollments.length === 1 ? eligibleEnrollments[0] : null);
  const visibleReportCard = selectedEnrollment
    && reportCard?.student?.id === id
    && reportCard?.session?.id === sessionId
    && reportCard?.term === term
    && reportCard?.class?.id === selectedEnrollment.class_id
    && (reportCard?.class?.arm_id || null) === (selectedEnrollment.arm_id || null)
    ? reportCard : null;

  if (loading || !child) {
    return <div className="flex items-center justify-center py-24 text-[#475569]"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading dashboard…</div>;
  }

  return (
    <div data-testid="child-dashboard">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-[#1B2C5A] via-[#243671] to-[#1B2C5A] p-5 sm:p-8 text-white relative overflow-hidden mb-6 shadow-xl">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#9DCD3F]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#9DCD3F]/10 rounded-full blur-3xl" />
        <button onClick={() => navigate("/parent")} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white transition mb-4" data-testid="cd-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to my children
        </button>
        <div className="flex items-start gap-5 relative z-10 flex-wrap">
          {child.photo ? (
            <img src={child.photo} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-white/20 shadow-xl" />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[#9DCD3F] border-4 border-white/20 text-[#1B2C5A] grid place-items-center font-display text-3xl shadow-xl">
              {(child.first_name?.[0] || child.full_name?.[0] || "?").toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#9DCD3F] font-medium">Student Profile</div>
            <h1 className="font-display text-2xl sm:text-4xl mt-1 leading-tight truncate">{child.full_name}</h1>
            <div className="text-sm text-white/70 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
              <span className="font-mono">{child.admission_no}</span>
              {child.gender && <span className="capitalize">· {child.gender}</span>}
              {child.date_of_birth && <span>· Born {child.date_of_birth}</span>}
            </div>
            {/* Program chips — student can be enrolled concurrently in multiple programs */}
            {enrollments.filter((e) => e.status === "active").length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="cd-programs">
                {enrollments.filter((e) => e.status === "active").map((e) => (
                  <span key={e.id} className="text-[10px] px-2.5 py-1 rounded-full bg-white/15 border border-[#9DCD3F]/30 text-white backdrop-blur-sm" data-testid={`prog-${e.id}`}>
                    <span className="text-[#9DCD3F] font-medium">{e.program_name || e.program_code || "Program"}</span>
                    <span className="text-white/70 mx-1">·</span>
                    <span>{e.class_name || "—"}{e.arm_name ? ` · ${e.arm_name}` : ""}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center w-full sm:w-auto sm:ml-auto">
            <Select value={sessionId} onValueChange={(value) => { setEnrollmentId(""); setSessionId(value); }}>
              <SelectTrigger className="h-9 flex-1 sm:w-40 bg-white/10 border-white/20 text-white text-xs" data-testid="cd-session"><SelectValue placeholder="Session" /></SelectTrigger>
              <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_current ? " (current)" : ""}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={term} onValueChange={setTerm}>
              <SelectTrigger className="h-9 flex-1 sm:w-32 bg-white/10 border-white/20 text-white text-xs" data-testid="cd-term"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">First Term</SelectItem>
                <SelectItem value="second">Second Term</SelectItem>
                <SelectItem value="third">Third Term</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPI Row inside hero */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 relative z-10">
          <HeroKpi icon={<Award className="w-5 h-5" />} label="Overall Avg" value={visibleReportCard ? `${visibleReportCard.overall.average_pct}%` : "—"} sub={visibleReportCard?.overall?.grade || (rcStatus === "draft" ? "Awaiting publish" : "No data")} />
          <HeroKpi icon={<GraduationCap className="w-5 h-5" />} label="Class Position" value={visibleReportCard?.overall?.position_ordinal || "—"} sub={visibleReportCard?.overall?.class_size ? `of ${visibleReportCard.overall.class_size}` : ""} />
          <HeroKpi icon={<Calendar className="w-5 h-5" />} label="Attendance" value={attendance ? `${Math.round(100 * (attendance.counts.present + attendance.counts.late) / Math.max(1, attendance.total))}%` : "—"} sub={attendance ? `${attendance.total} school days` : ""} />
          <HeroKpi icon={<Wallet className="w-5 h-5" />} label="Finance" value="View account" sub="Placement-specific balances" />
        </div>
      </div>

      {/* Tabs nav */}
      <div className="mb-5 flex gap-1 overflow-x-auto pb-1 border-b border-[#E2E8F0]" data-testid="cd-tabs">
        {[
          ["overview", "Overview", Sparkles],
          ["academic", "Academic", Award],
          ["attendance", "Attendance", Calendar],
          ["timetable", "Timetable", CalendarClock],
          ["homework", "Homework", ClipboardList],
          ["fees", "Fees", Wallet],
          ["behaviour", "Behaviour", ShieldCheck],
          ["notifications", "Announcements", Bell],
          ["info", "Info & Health", Heart],
          ["insights", "Insights", TrendingUp],
        ].map(([slug, label, Icon]) => (
          <NavLink key={slug} to={`/parent/child/${id}/${slug}`}
            className={({ isActive }) => "shrink-0 inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg transition " + (isActive ? "bg-[#1B2C5A] text-white" : "text-[#475569] hover:bg-[#9DCD3F]/10 hover:text-[#1B2C5A]")}
            data-testid={`cd-tab-${slug}`} end={slug === "overview"}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </NavLink>
        ))}
      </div>

      {/* Outlet — but pass context via URL params + refetch, or lift child to context */}
      <ChildContext.Provider value={{ child, reportCard: visibleReportCard, attendance, invoices, notifications, sessionId, term, rcStatus, enrollments, eligibleEnrollments, selectedEnrollment, enrollmentId, setEnrollmentId, refetch: load }}>
        <Outlet />
      </ChildContext.Provider>
    </div>
  );
};

/* Sub-context so tabs can use the fetched data without re-fetching */
const ChildContext = createContext(null);
export const useChildCtx = () => useContext(ChildContext);

/* ========= Building blocks ========= */
const HeroKpi = ({ icon, label, value, sub }) => (
  <div className="rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm p-3 hover:bg-white/15 transition">
    <div className="flex items-center gap-2 text-[#9DCD3F]">{icon}<span className="text-[10px] uppercase tracking-wider">{label}</span></div>
    <div className="font-display text-xl sm:text-2xl mt-1 leading-none truncate">{value}</div>
    <div className="text-[10px] text-white/60 mt-1 truncate">{sub}</div>
  </div>
);

/* ========= Tabs ========= */

export const OverviewTab = () => {
  const { reportCard, attendance, invoices, rcStatus, child, notifications } = useChildCtx();
  // Attendance chart data - last 30 records
  const attSeries = useMemo(() => {
    if (!attendance?.records) return [];
    return [...attendance.records].reverse().slice(-30).map((r) => ({
      date: r.date?.slice(5) || "",
      value: r.status === "present" ? 1 : r.status === "late" ? 0.5 : 0,
      status: r.status,
    }));
  }, [attendance]);

  const donutData = attendance ? [
    { name: "Present", value: attendance.counts.present, color: EMERALD },
    { name: "Late", value: attendance.counts.late, color: AMBER },
    { name: "Absent", value: attendance.counts.absent, color: RED },
    { name: "Excused", value: attendance.counts.excused, color: NAVY },
  ].filter((d) => d.value > 0) : [];

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="tab-overview">
      {/* attendance trend */}
      <Card className="p-5 border-[#E2E8F0] lg:col-span-2">
        <SectionHead icon={<Calendar className="w-4 h-4" />} title="Attendance Trend (Last 30 days)" />
        {attSeries.length === 0 ? (
          <EmptyBlock text="No attendance recorded yet for this term." />
        ) : (
          <div className="h-64 mt-2">
            <ResponsiveContainer>
              <AreaChart data={attSeries}>
                <defs>
                  <linearGradient id="atFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={LIME} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={LIME} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} />
                <YAxis domain={[0, 1]} tick={{ fontSize: 10, fill: "#475569" }} ticks={[0, 0.5, 1]} tickFormatter={(v) => v === 1 ? "P" : v === 0.5 ? "L" : "A"} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(_, __, p) => [p.payload.status, "Status"]} />
                <Area type="monotone" dataKey="value" stroke={LIME} strokeWidth={2} fill="url(#atFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* attendance donut */}
      <Card className="p-5 border-[#E2E8F0]">
        <SectionHead icon={<CalendarCheck className="w-4 h-4" />} title="Attendance Split" />
        {donutData.length === 0 ? (
          <EmptyBlock text="No records" />
        ) : (
          <div className="h-56 mt-2">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={donutData} dataKey="value" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* subject performance */}
      <Card className="p-5 border-[#E2E8F0] lg:col-span-2">
        <SectionHead icon={<BookOpen className="w-4 h-4" />} title="Subject Performance" />
        {rcStatus === "draft" ? (
          <EmptyBlock text="Results have not been published yet for this term. You'll see per-subject scores once the school publishes." />
        ) : rcStatus === "loading" ? (
          <EmptyBlock text="Loading academic results…" />
        ) : rcStatus === "select-programme" || rcStatus === "ambiguous" ? (
          <EmptyBlock text="Select a programme in the Academic tab to view its report." />
        ) : rcStatus === "forbidden" ? (
          <EmptyBlock text="You do not have access to this academic report." />
        ) : rcStatus === "notfound" ? (
          <EmptyBlock text="No report was found for this session and term." />
        ) : rcStatus === "error" ? (
          <EmptyBlock text="Academic results could not be loaded. Please try again later." />
        ) : !reportCard?.subjects?.length ? (
          <EmptyBlock text="No subjects graded yet." />
        ) : (
          <div className="h-72 mt-2">
            <ResponsiveContainer>
              <BarChart data={reportCard.subjects.map((s) => ({ name: (s.subject_name || "").slice(0, 12), value: s.total, class_avg: s.class_average || 0 }))}>
                <CartesianGrid vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#475569" }} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="value" name={`${child.first_name || "Score"}`} fill={LIME} radius={[6, 6, 0, 0]} />
                <Bar dataKey="class_avg" name="Class Avg" fill={NAVY} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Announcements preview */}
      <Card className="p-5 border-[#E2E8F0]">
        <SectionHead icon={<Bell className="w-4 h-4" />} title="Latest Announcements" />
        {notifications.length === 0 ? (
          <EmptyBlock text="No announcements yet." />
        ) : (
          <div className="space-y-2 mt-1 max-h-72 overflow-y-auto pr-1">
            {notifications.slice(0, 5).map((n) => (
              <div key={n.id} className="rounded-lg border border-[#E2E8F0] p-2.5 hover:border-[#9DCD3F]/40 transition">
                <div className="text-xs font-medium text-[#1B2C5A]">{n.title}</div>
                <div className="text-[11px] text-[#475569] mt-0.5 line-clamp-2">{n.body || n.message}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export const AcademicTab = () => {
  const { reportCard, rcStatus, sessionId, term, child, eligibleEnrollments, selectedEnrollment, enrollmentId, setEnrollmentId } = useChildCtx();
  const contextKey = `${child.id}:${sessionId}:${term}:${selectedEnrollment?.id || ""}`;
  const currentContext = useRef(contextKey);
  const pdfRequest = useRef(null);
  currentContext.current = contextKey;
  useLayoutEffect(() => () => {
    pdfRequest.current?.abort();
    pdfRequest.current = null;
  }, [contextKey]);
  const programmeSelector = eligibleEnrollments.length > 1 ? (
    <Card className="p-4 sm:p-5 border-[#9DCD3F]/40" data-testid="cd-programme-selector">
      <label htmlFor="cd-academic-programme" className="block text-sm font-medium text-[#1B2C5A]">Programme for this session</label>
      <p className="text-xs text-[#475569] mt-1 mb-3">Choose the programme whose report you want to view.</p>
      <Select value={enrollmentId} onValueChange={setEnrollmentId}>
        <SelectTrigger id="cd-academic-programme" className="w-full sm:max-w-sm" data-testid="cd-program"><SelectValue placeholder="Select a programme" /></SelectTrigger>
        <SelectContent>
          {eligibleEnrollments.map((e) => (
            <SelectItem key={e.id} value={e.id}>
              {e.program_name || e.program_code || "Programme"} — {e.class_name || "Class"}{e.arm_name ? ` ${e.arm_name}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Card>
  ) : null;
  const showMessage = (text) => <div className="space-y-4" data-testid="tab-academic">{programmeSelector}<EmptyBlock text={text} /></div>;
  const downloadPdf = () => {
    if (!reportCard || !selectedEnrollment) return;
    pdfRequest.current?.abort();
    const controller = new AbortController();
    pdfRequest.current = controller;
    const requestedContext = contextKey;
    api.get(`/assessments/report-card/${child.id}/pdf`, {
      params: { session_id: sessionId, term, program_id: selectedEnrollment.program_id, class_id: selectedEnrollment.class_id, arm_id: selectedEnrollment.arm_id },
      responseType: "blob",
      signal: controller.signal,
    })
      .then((res) => {
        if (controller.signal.aborted || pdfRequest.current !== controller || currentContext.current !== requestedContext) return;
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `ReportCard_${child.admission_no}_${term}.pdf`;
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }).catch((e) => {
        if (controller.signal.aborted || pdfRequest.current !== controller || currentContext.current !== requestedContext) return;
        toast.error(e?.response?.status === 409 ? "Please select a programme before downloading the report." : formatApiError(e));
      }).finally(() => { if (pdfRequest.current === controller) pdfRequest.current = null; });
  };

  if (rcStatus === "loading") return showMessage("Loading academic results…");
  if (rcStatus === "select-programme") return showMessage("Select a programme above to view its report for this session.");
  if (rcStatus === "ambiguous") return showMessage("More than one programme matches this session. Please select a programme above.");
  if (rcStatus === "notfound") return showMessage("No report was found for this session and term. Please contact the school if you expected one.");
  if (rcStatus === "forbidden") return showMessage("You do not have access to this academic report.");
  if (rcStatus === "error") return showMessage("Academic results could not be loaded. Please try again later.");
  if (rcStatus === "draft") {
    return (
      <div className="space-y-4" data-testid="tab-academic">
        {programmeSelector}
        <Card className="p-10 text-center border-amber-200 bg-amber-50/40" data-testid="tab-academic-draft">
          <ClipboardCheck className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <h3 className="font-display text-xl text-amber-900">Results are being prepared</h3>
          <p className="text-sm text-amber-800 mt-2 max-w-md mx-auto">
            The school hasn&apos;t published the {term === "first" ? "First" : term === "second" ? "Second" : "Third"} Term results yet. You will be notified as soon as they are ready.
          </p>
        </Card>
      </div>
    );
  }
  if (!reportCard) return showMessage("The selected report is not available yet. Please try again.");

  return (
    <div className="space-y-4" data-testid="tab-academic">
      {programmeSelector}
      {/* summary tiles */}
      <div className="grid md:grid-cols-4 gap-3">
        <BigKpi label="Total" value={reportCard.overall.total} suffix={reportCard.overall.subjects_taken ? `/ ${reportCard.overall.subjects_taken * 100}` : ""} />
        <BigKpi label="Average" value={`${reportCard.overall.average_pct}%`} suffix={reportCard.overall.grade} />
        <BigKpi label="Position" value={reportCard.overall.position_ordinal || "—"} suffix={reportCard.overall.class_size ? `of ${reportCard.overall.class_size}` : ""} />
        <BigKpi label="Class Avg" value={`${reportCard.overall.class_average_pct ?? 0}%`} suffix="peer avg" />
      </div>

      <Card className="p-5 border-[#E2E8F0]">
        <div className="flex items-center gap-3 mb-3">
          <SectionHead icon={<Award className="w-4 h-4" />} title="Subject Breakdown" />
          <Button size="sm" onClick={downloadPdf} className="ml-auto bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="cd-pdf-btn"><FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#475569] uppercase tracking-wider border-b border-[#E2E8F0]">
                <th className="py-2 pr-2">Subject</th>
                <th className="text-center px-2">CA1</th>
                <th className="text-center px-2">CA2</th>
                <th className="text-center px-2">Exam</th>
                <th className="text-center px-2">Att.</th>
                <th className="text-center px-2">Total</th>
                <th className="text-center px-2">Grade</th>
                <th className="text-left px-2">Remark</th>
                <th className="text-center px-2">Position</th>
              </tr>
            </thead>
            <tbody>
              {reportCard.subjects.map((s) => (
                <tr key={s.subject_id} className="border-b border-[#E2E8F0] last:border-0" data-testid={`ac-sub-${s.subject_id}`}>
                  <td className="py-2 pr-2 font-medium text-[#1B2C5A]">{s.subject_name}</td>
                  <td className="text-center px-2">{s.ca1 || "—"}</td>
                  <td className="text-center px-2">{s.ca2 || "—"}</td>
                  <td className="text-center px-2">{s.exam || "—"}</td>
                  <td className="text-center px-2">{s.attendance_marks}</td>
                  <td className="text-center px-2 font-bold">{s.total}</td>
                  <td className="text-center px-2"><span className="inline-block text-xs px-2 py-0.5 rounded-full bg-[#9DCD3F]/20 text-[#1B2C5A] font-medium">{s.grade}</span></td>
                  <td className="px-2 text-[#475569]">{s.remark}</td>
                  <td className="text-center px-2">{s.position_ordinal || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {(reportCard.comments?.class_teacher || reportCard.comments?.head_teacher) && (
        <div className="grid md:grid-cols-2 gap-3">
          {reportCard.comments.class_teacher && (
            <Card className="p-4 border-[#E2E8F0] bg-[#FDFBF7]" data-testid="ac-comment-ct">
              <div className="flex items-center gap-2 mb-2"><MessageCircle className="w-4 h-4 text-[#9DCD3F]" /><span className="text-xs font-medium text-[#1B2C5A] uppercase tracking-wider">Class Teacher</span></div>
              <p className="text-sm text-[#1B2C5A] italic">&ldquo;{reportCard.comments.class_teacher}&rdquo;</p>
            </Card>
          )}
          {reportCard.comments.head_teacher && (
            <Card className="p-4 border-[#E2E8F0] bg-[#FDFBF7]" data-testid="ac-comment-ht">
              <div className="flex items-center gap-2 mb-2"><MessageCircle className="w-4 h-4 text-[#9DCD3F]" /><span className="text-xs font-medium text-[#1B2C5A] uppercase tracking-wider">Head Teacher</span></div>
              <p className="text-sm text-[#1B2C5A] italic">&ldquo;{reportCard.comments.head_teacher}&rdquo;</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export const AttendanceTab = () => {
  const { attendance } = useChildCtx();
  if (!attendance || attendance.total === 0) return <EmptyBlock text="No attendance records yet." />;
  const pct = Math.round(100 * (attendance.counts.present + attendance.counts.late) / attendance.total);
  const tone = pct >= 90 ? EMERALD : pct >= 75 ? LIME : pct >= 60 ? AMBER : RED;
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="tab-attendance">
      <Card className="p-5 border-[#E2E8F0] text-center">
        <SectionHead icon={<Calendar className="w-4 h-4" />} title="Overall Attendance" />
        <div className="h-56 mt-2">
          <ResponsiveContainer>
            <RadialBarChart innerRadius="60%" outerRadius="100%" data={[{ name: "att", value: pct, fill: tone }]} startAngle={90} endAngle={-270}>
              <RadialBar minAngle={5} clockWise dataKey="value" cornerRadius={20} />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
        <div className="-mt-32 text-center">
          <div className="font-display text-4xl text-[#1B2C5A]">{pct}%</div>
          <div className="text-xs text-[#475569] mt-1">{attendance.total} days · {attendance.counts.present} present, {attendance.counts.late} late, {attendance.counts.absent} absent</div>
        </div>
      </Card>
      <Card className="p-5 border-[#E2E8F0] lg:col-span-2">
        <SectionHead icon={<CalendarCheck className="w-4 h-4" />} title="Recent Attendance Records" />
        <div className="max-h-80 overflow-y-auto mt-2 divide-y divide-[#E2E8F0]">
          {attendance.records.map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`at-row-${r.id}`}>
              <span className="font-mono text-xs text-[#475569] w-24">{r.date}</span>
              <span className={"inline-block text-xs px-2 py-0.5 rounded-full font-medium " + (
                r.status === "present" ? "bg-emerald-100 text-emerald-800" :
                r.status === "late" ? "bg-amber-100 text-amber-800" :
                r.status === "excused" ? "bg-sky-100 text-sky-800" : "bg-red-100 text-red-800"
              )}>
                {r.status}
              </span>
              {r.note && <span className="text-xs text-[#475569] flex-1 truncate">— {r.note}</span>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export const FeesTab = () => {
  const { child, enrollments } = useChildCtx();
  return <div data-testid="tab-fees"><StudentFinancePanel student={child} enrollments={enrollments} parentMode /></div>;
};

export const NotificationsTab = () => {
  const { notifications } = useChildCtx();
  if (!notifications.length) return <EmptyBlock text="No announcements from the school yet." />;
  return (
    <div className="space-y-2" data-testid="tab-notifications">
      {notifications.map((n) => (
        <Card key={n.id} className="p-4 border-[#E2E8F0] hover:border-[#9DCD3F] transition" data-testid={`notif-${n.id}`}>
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-[#9DCD3F]/15 text-[#1B2C5A] grid place-items-center shrink-0"><Bell className="w-4 h-4" /></span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1B2C5A]">{n.title}</div>
              <div className="text-xs text-[#475569] mt-1">{n.body || n.message}</div>
              <div className="text-[10px] text-[#475569]/60 mt-1.5 uppercase tracking-wider">{n.created_at?.slice(0, 10) || ""}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export const InfoTab = () => {
  const { child, enrollments } = useChildCtx();
  const activeEnrollments = (enrollments || []).filter((e) => e.status === "active");
  const rows = [
    ["Full Name", child.full_name],
    ["Admission No", child.admission_no],
    ["Gender", child.gender],
    ["Date of Birth", child.date_of_birth],
    ["Blood Group", child.blood_group],
    ["Nationality", child.nationality],
    ["State of Origin", child.state_of_origin],
    ["LGA", child.lga],
    ["Address", child.address],
    ["Phone", child.phone],
    ["Medical Notes", child.medical_notes],
    ["Allergies", child.allergies],
    ["Guardian on File", child.guardian_name],
    ["Guardian Phone", child.guardian_phone],
  ].filter(([, v]) => v);
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="tab-info">
      {/* Enrolled Programs — first because it's the most important operational fact */}
      <Card className="p-5 border-[#E2E8F0] md:col-span-2" data-testid="info-programs">
        <SectionHead icon={<GraduationCap className="w-4 h-4" />} title={`Enrolled Programs (${activeEnrollments.length})`} />
        {activeEnrollments.length === 0 ? (
          <p className="text-sm text-[#475569] italic mt-3">No active enrollments on record.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {activeEnrollments.map((e) => (
              <div key={e.id} className="rounded-xl border border-[#9DCD3F]/30 bg-[#9DCD3F]/5 p-3.5" data-testid={`enroll-${e.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-[#9DCD3F] font-medium">{e.program_code || "Program"}</div>
                <div className="text-base font-display text-[#1B2C5A] leading-tight mt-0.5">{e.program_name || "—"}</div>
                <div className="text-xs text-[#475569] mt-1.5">
                  <span className="text-[#1B2C5A] font-medium">{e.class_name || "—"}</span>
                  {e.arm_name && <span> · Arm: <span className="text-[#1B2C5A] font-medium">{e.arm_name}</span></span>}
                </div>
                {e.enrollment_date && <div className="text-[10px] text-[#475569] mt-2">Enrolled {e.enrollment_date}</div>}
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-5 border-[#E2E8F0]">
        <SectionHead icon={<Heart className="w-4 h-4" />} title="Personal Information" />
        <dl className="mt-3 divide-y divide-[#E2E8F0]">
          {rows.map(([k, v]) => (
            <div key={k} className="py-2 flex gap-3 text-sm">
              <dt className="text-[#475569] w-40 shrink-0">{k}</dt>
              <dd className="text-[#1B2C5A] flex-1 break-words">{v}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <Card className="p-5 border-[#E2E8F0]">
        <SectionHead icon={<Info className="w-4 h-4" />} title="Emergency & School Contact" />
        <div className="text-sm text-[#475569] space-y-2 mt-3">
          <p><strong className="text-[#1B2C5A]">School Office:</strong> +234 703 616 1413 · +234 700 315 8938</p>
          <p><strong className="text-[#1B2C5A]">Website:</strong> www.dfa.com.ng</p>
          <p><strong className="text-[#1B2C5A]">Address:</strong> Shehu Idris Road, Emanto by Kabama Layout, Zaria, Kaduna State.</p>
          <p className="text-xs text-[#475569] mt-4">For urgent updates about your child, contact the school office during working hours.</p>
        </div>
      </Card>
    </div>
  );
};

// Coming-soon panels for features not yet built
export const TimetableTab = () => <ComingSoon icon={<CalendarClock />} title="Timetable" description="Your child's weekly class timetable will appear here once the school publishes it." />;
export const HomeworkTab = () => <ComingSoon icon={<ClipboardList />} title="Homework & Assignments" description="Homework given to your child's class will be listed here." />;
export const BehaviourTab = () => <ComingSoon icon={<ShieldCheck />} title="Behaviour & Discipline" description="Behaviour observations, discipline notes and character reports will be published here." />;

/* ============ Small helpers ============ */
const SectionHead = ({ icon, title }) => (
  <div className="flex items-center gap-2">
    <span className="text-[#9DCD3F]">{icon}</span>
    <h3 className="font-display text-base text-[#1B2C5A]">{title}</h3>
  </div>
);
const BigKpi = ({ label, value, suffix, tone }) => {
  const toneCls = tone === "good" ? "border-emerald-200 bg-emerald-50/40" : tone === "warn" ? "border-amber-200 bg-amber-50/40" : "border-[#E2E8F0] bg-white";
  return (
    <Card className={"p-4 " + toneCls}>
      <div className="text-[10px] uppercase tracking-wider text-[#9DCD3F] font-medium">{label}</div>
      <div className="font-display text-2xl text-[#1B2C5A] mt-1">{value}</div>
      {suffix && <div className="text-xs text-[#475569] mt-0.5">{suffix}</div>}
    </Card>
  );
};
const EmptyBlock = ({ text }) => <div className="text-center py-8 text-sm text-[#475569] italic">{text}</div>;
const ComingSoon = ({ icon, title, description }) => (
  <Card className="p-10 text-center border-dashed border-[#9DCD3F]/40 bg-[#9DCD3F]/5" data-testid={`cs-${title.split(" ")[0].toLowerCase()}`}>
    <div className="w-14 h-14 mx-auto rounded-2xl bg-white border border-[#9DCD3F]/30 grid place-items-center text-[#9DCD3F] mb-3">{icon}</div>
    <h3 className="font-display text-xl text-[#1B2C5A]">{title}</h3>
    <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">{description}</p>
    <span className="inline-block text-[10px] px-2.5 py-1 rounded-full bg-[#9DCD3F]/20 text-[#1B2C5A] font-medium mt-4 uppercase tracking-wider">Coming Soon</span>
  </Card>
);

export default ChildDashboard;
